import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTimer } from './contexts/TimerContext';
import { useStreak } from './contexts/StreakContext';
import { useAuth } from './common/AuthContext';
import { toast } from 'react-toastify';
import ShareDialog from './components/ShareDialog';
import './css/Timer.css';

const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatDuration = (totalSeconds, t) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    if (h > 0) {
        return `${h}${t('hours')} ${m}${t('minutes')}`;
    }
    return `${m}${t('minutes')}`;
};

const formatTimeOfDay = (dateStr) => {
    const d = new Date(dateStr);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function Timer() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        activeSession,
        todaySessions,
        todaySummary,
        displaySeconds,
        startSession,
        pauseSession,
        resumeSession,
        stopSession
    } = useTimer();

    const { checkStreak } = useStreak();
    const { user } = useAuth();
    const [confirmStop, setConfirmStop] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareData, setShareData] = useState({});
    const scheduleInfo = location.state;

    const handleStart = async () => {
        if (!scheduleInfo) return;
        try {
            await startSession(
                scheduleInfo.subject_id,
                scheduleInfo.schedule_id,
                {
                    subject_name: scheduleInfo.subject_name,
                    special_text: scheduleInfo.special_text,
                    subject_color: scheduleInfo.subject_color,
                    start_time: scheduleInfo.start_time,
                    scheduled_time: scheduleInfo.scheduled_time
                }
            );
        } catch (error) {
            console.error('Timer start error:', error);
            toast.error(error.message || t('error.save'));
        }
    };

    const handlePause = () => {
        pauseSession();
    };

    const handleResume = () => {
        resumeSession();
    };

    const handleStop = async () => {
        if (!confirmStop) {
            setConfirmStop(true);
            return;
        }
        const sessionSeconds = displaySeconds;
        await stopSession();
        setConfirmStop(false);
        checkStreak();

        // Build share data from today's sessions
        const today = new Date();
        const dateStr = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;
        const totalToday = todaySummary.reduce((sum, s) => sum + s.actual_seconds, 0) + sessionSeconds;
        const subjectList = todaySummary.map(s => ({
            name: s.subject_name,
            color: s.subject_color,
            seconds: s.actual_seconds
        }));
        setShareData({ totalSeconds: totalToday, subjects: subjectList, date: dateStr, username: user?.username || '' });
        setShareDialogOpen(true);
    };

    const handleCancelStop = () => {
        setConfirmStop(false);
    };

    const currentSubjectName = activeSession?.subject_name || scheduleInfo?.subject_name;
    const currentSpecialText = activeSession?.special_text || scheduleInfo?.special_text;
    const currentSubjectColor = activeSession?.subject_color || scheduleInfo?.subject_color;
    const currentScheduleStart = activeSession?.schedule_start || scheduleInfo?.start_time;
    const currentScheduleDuration = activeSession?.schedule_duration || scheduleInfo?.scheduled_time;
    const currentScheduleType = activeSession?.schedule_type || scheduleInfo?.schedule_type;
    const isTodo = currentScheduleType === 'todo';
    const isActive = activeSession?.status === 'active';
    const isPaused = activeSession?.status === 'paused';
    const hasSession = !!activeSession;

    // Calculate total today study time
    const totalTodaySeconds = todaySummary.reduce((sum, item) => sum + item.actual_seconds, 0);

    return (
        <div className="timer-container">
            <div className="timer-header">
                <button className="timer-back-btn" onClick={() => navigate('/timetable')}>
                    {t('timer.backToTimetable')}
                </button>
            </div>

            {!hasSession && !scheduleInfo && (
                <div className="timer-no-session">
                    <p>{t('timer.noActiveSession')}</p>
                </div>
            )}

            {(hasSession || scheduleInfo) && (
                <div className="timer-main">
                    {currentSubjectName && (
                        <div className="timer-subject-info">
                            <span className="timer-subject-color-box" style={{ backgroundColor: currentSubjectColor }} />
                            <span className="timer-subject-name">
                                {currentSpecialText || currentSubjectName}
                                {currentSpecialText && (
                                    <span className="timer-subject-original"> ({currentSubjectName})</span>
                                )}
                            </span>
                        </div>
                    )}

                    {isTodo ? (
                        <div className="timer-schedule-info" style={{ color: 'var(--link-color)' }}>
                            {t('todo.title')}
                        </div>
                    ) : (
                        currentScheduleStart && currentScheduleDuration && (
                            <div className="timer-schedule-info">
                                {t('timer.planned')}: {formatTimeOfDay(currentScheduleStart)} - {currentScheduleDuration}{t('minutes')}
                            </div>
                        )
                    )}

                    <div className="timer-display">
                        <div className={`timer-time ${isPaused ? 'paused' : ''}`}>
                            {formatTime(displaySeconds)}
                        </div>
                    </div>

                    <div className="timer-controls">
                        {!hasSession && (
                            <button className="timer-btn start" onClick={handleStart}>
                                {t('timer.start')}
                            </button>
                        )}
                        {isActive && (
                            <button className="timer-btn pause" onClick={handlePause}>
                                {t('timer.pause')}
                            </button>
                        )}
                        {isPaused && (
                            <button className="timer-btn resume" onClick={handleResume}>
                                {t('timer.resume')}
                            </button>
                        )}
                        {hasSession && !confirmStop && (
                            <button className="timer-btn stop" onClick={handleStop}>
                                {t('timer.stop')}
                            </button>
                        )}
                        {hasSession && confirmStop && (
                            <div className="timer-confirm-stop">
                                <span>{t('timer.confirmStop')}</span>
                                <button className="timer-btn stop" onClick={handleStop}>
                                    {t('timer.stop')}
                                </button>
                                <button className="timer-btn cancel" onClick={handleCancelStop}>
                                    {t('cancel')}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="timer-stats">
                        <span>{t('timer.pureStudyTime')}: {formatDuration(displaySeconds, t)}</span>
                        <span>{t('timer.pauses')}: {activeSession?.pause_count || 0}</span>
                    </div>
                </div>
            )}

            <div className="timer-today-section">
                <h3>{t('timer.todaySummary')}</h3>
                {todaySummary.length > 0 ? (
                    <div className="timer-summary-list">
                        {todaySummary.map((item, index) => (
                            <div key={index} className="timer-summary-item">
                                <div className="timer-summary-subject">
                                    <div className="timer-summary-color" style={{ backgroundColor: item.subject_color }} />
                                    <span>{item.subject_name}</span>
                                </div>
                                <div className="timer-summary-bar-wrapper">
                                    <div
                                        className="timer-summary-bar"
                                        style={{
                                            width: `${totalTodaySeconds > 0 ? (item.actual_seconds / totalTodaySeconds) * 100 : 0}%`,
                                            backgroundColor: item.subject_color
                                        }}
                                    />
                                </div>
                                <span className="timer-summary-time">
                                    {formatDuration(item.actual_seconds, t)}
                                </span>
                            </div>
                        ))}
                        <div className="timer-summary-total">
                            {t('timer.total')}: {formatDuration(totalTodaySeconds, t)}
                        </div>
                    </div>
                ) : (
                    <p className="timer-no-data">{t('timer.noData')}</p>
                )}

                <h3>{t('timer.sessionTimeline')}</h3>
                {todaySessions.length > 0 ? (
                    <div className="timer-timeline">
                        {todaySessions.map((session, index) => (
                            <div key={index} className="timer-timeline-item">
                                <div className="timer-timeline-color" style={{ backgroundColor: session.study_subject?.color || '#ccc' }} />
                                <span className="timer-timeline-time">
                                    {formatTimeOfDay(session.start_time)}
                                    {session.end_time && ` - ${formatTimeOfDay(session.end_time)}`}
                                </span>
                                <span className="timer-timeline-subject">
                                    {session.study_subject?.subjectname || 'Unknown'}
                                </span>
                                <span className="timer-timeline-duration">
                                    ({formatDuration(session.total_seconds, t)})
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="timer-no-data">{t('timer.noData')}</p>
                )}
            </div>

            {/* Share Dialog after session stop */}
            <ShareDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                cardType="daily"
                data={shareData}
            />
        </div>
    );
}

export default Timer;
